import { z } from 'zod';

export const FILTER_OPERATORS = [
  'eq', 'ne', 'gt', 'lt', 'gte', 'lte', 'between', 'in', 'contains', 'starts_with',
] as const;
export type FilterOperator = (typeof FILTER_OPERATORS)[number];

export const AGGREGATE_FNS = ['sum', 'count', 'avg', 'min', 'max'] as const;
export type AggregateFn = (typeof AGGREGATE_FNS)[number];

export const reportFilterSchema = z.object({
  field: z.string().min(1).max(60),
  operator: z.enum(FILTER_OPERATORS),
  /** Single value, or an array for `in` (list) and `between` ([min, max]). */
  value: z.union([z.string(), z.array(z.string()).max(100)]).optional(),
});
export type ReportFilter = z.infer<typeof reportFilterSchema>;

export const reportMeasureSchema = z.object({
  field: z.string().min(1).max(60),
  aggregate: z.enum(AGGREGATE_FNS),
});
export type ReportMeasure = z.infer<typeof reportMeasureSchema>;

export const reportConfigSchema = z.object({
  dataSource: z.string().min(1).max(60),
  filters: z.array(reportFilterSchema).max(20).default([]),
  groupBy: z.array(z.string().min(1).max(60)).max(6).default([]),
  measures: z.array(reportMeasureSchema).max(8).default([]),
  limit: z.number().int().min(1).max(10000).optional(),
});
export type ReportConfig = z.infer<typeof reportConfigSchema>;

export const runReportInputSchema = z.object({ config: reportConfigSchema });
export type RunReportInput = z.infer<typeof runReportInputSchema>;

export const saveReportInputSchema = z.object({
  name: z.string().trim().min(1).max(120),
  config: reportConfigSchema,
});
export type SaveReportInput = z.infer<typeof saveReportInputSchema>;

// ── Catalog (sent to the builder UI; no SQL exposed) ─────────────────────────

export interface CatalogField {
  key: string;
  label: string;
  type: 'date' | 'text' | 'numeric' | 'enum';
  capabilities: ('filter' | 'group' | 'measure')[];
  validOperators: FilterOperator[];
  validAggregates: AggregateFn[];
  enumValues: string[];
}
export interface CatalogDataSource {
  key: string;
  label: string;
  fields: CatalogField[];
}

// ── Run result ────────────────────────────────────────────────────────────────

export interface ReportColumn {
  key: string;
  label: string;
  type: 'date' | 'text' | 'numeric' | 'enum';
  isMeasure: boolean;
}
export interface ReportRunResult {
  columns: ReportColumn[];
  /** Row cells aligned to columns; money/measure cells are decimal strings. */
  rows: (string | null)[][];
  totals: (string | null)[]; // measure columns summed; dimension cells null
  rowCount: number;
  truncated: boolean;
  mode: 'grouped' | 'detail';
}

export interface SavedReport {
  id: string;
  companyId: string;
  name: string;
  config: ReportConfig;
  createdAt: string;
  updatedAt: string;
}
